import React from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import SubPageTitle from 'components/_common/panel/SubPageTitle';
import { useProgramPanel } from 'contexts/programPanelContext';
import { knowledgeBaseApi } from 'api/knowledgeBase';
import { Loader } from 'components/_new/Loader';
import { ErrorContainer } from 'components/_new/ErrorContainer/ErrorContainer';
import { Button } from 'components/_new/Button';
import { useKnowledgeBase } from 'components/knowledgeBase/knowledgeBaseContext';
import { ModifyArticleForm } from './components/ModifyArticleForm';

export const ProgramKnowledgeBaseArticleEdit = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const { generateDirectoryLink } = useKnowledgeBase();
	const { data: program } = useProgramPanel();
	const params = useParams();
	const articleId = Number(params?.articleId);

	const hasArticle = Boolean(articleId);
	const {
		data: knowledgeBaseArticle,
		isLoading: knowledgeBaseArticleLoading,
		isSuccess: knowledgeBaseArticleSuccess,
		isError: knowledgeBaseArticlesIsError,
		error: knowledgeBaseArticlesError,
		refetch: knowledgeBaseArticlesRefetch,
		remove: knowledgeBaseArticleRemove,
	} = useQuery(
		['KNOWLEDGE_BASE_PP_ARTICLE', { programId: Number(program?.id), articleId }],
		() => knowledgeBaseApi.program.getProgramArticleById(Number(program?.id), articleId!),
		{
			enabled: hasArticle,
		}
	);

	const handleBackClick = () => {
		if (knowledgeBaseArticleSuccess && knowledgeBaseArticle?.directoryId) {
			navigate({
				pathname: generateDirectoryLink(knowledgeBaseArticle.directoryId),
			});
		}
	};

	if (hasArticle && knowledgeBaseArticlesIsError) {
		return <ErrorContainer action={knowledgeBaseArticlesRefetch} error={knowledgeBaseArticlesError} />;
	}

	if (hasArticle && knowledgeBaseArticleLoading) {
		return <Loader />;
	}

	if (hasArticle && knowledgeBaseArticleSuccess) {
		return (
			<>
				<Helmet title={t('knowledgeBase.programPanel.editArticle')} />
				<SubPageTitle
					title={t('knowledgeBase.programPanel.editArticle')}
					additional={program?.displayName}
					right={() => (
						<Button
							icon="arrow-left"
							variant="primary-text"
							variantSize="sm"
							onClick={handleBackClick}
							label={t('actions.back')}
						/>
					)}
				/>

				<ModifyArticleForm
					article={knowledgeBaseArticle}
					onSave={() => {
						knowledgeBaseArticleRemove();
					}}
				/>
			</>
		);
	}

	return null;
};
